import { useMemo, useState, useCallback, useEffect } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Button,
  Input,
  Collapse,
} from "reactstrap";

const PropertySelector = ({
  propertyList,
  selectedFilters,
  onPropertySelectionChange,
  onAddClick,
  onExportExcel,
  isLoading,
  user,
  distinctActions = [],
  distinctCompletedBy = [],     // ← NEW prop
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const currentProperty = useMemo(
    () => propertyList.find((prop) => prop._id === selectedFilters.property_id),
    [propertyList, selectedFilters.property_id]
  );

  const buildingList = useMemo(() => {
    if (!currentProperty?.building) return [];
    return currentProperty.building
      .filter((b) => b && b.buildingName)
      .map((b) => b.buildingName);
  }, [currentProperty]);

  const propertyOptions = useMemo(
    () =>
      propertyList.map((prop) => ({
        value: prop._id,
        label: `${prop.title}, ${prop.location || "No location"}`,
      })),
    [propertyList]
  );

  const buildingOptions = useMemo(
    () =>
      buildingList.map((building) => ({
        value: building,
        label: building,
      })),
    [buildingList]
  );

  const actionOptions = useMemo(
    () => distinctActions.map((act) => ({ value: act, label: act })),
    [distinctActions]
  );

  // NEW: Prepare options for Completed By (users who completed logs)
  const completedByOptions = useMemo(
    () => distinctCompletedBy.map((user) => ({
      value: user.object_id,
      label: `${user.name} (${user.role})`,
    })),
    [distinctCompletedBy]
  );

  // Fixed roles (matching backend model roles)
  const roleOptions = useMemo(
    () => [
      { value: 'Company', label: 'Company' },
      { value: 'Manager', label: 'Manager' },
      { value: 'Vendor', label: 'Vendor' },
      { value: 'TechnicalStaff', label: 'Technical Staff' },
    ],
    []
  );

  const selectedPropertyOption = useMemo(
    () =>
      propertyOptions.find((opt) => opt.value === selectedFilters.property_id) || null,
    [propertyOptions, selectedFilters.property_id]
  );

  const selectedBuildingOption = useMemo(
    () =>
      buildingOptions.find((opt) => opt.value === selectedFilters.building_name) || null,
    [buildingOptions, selectedFilters.building_name]
  );

  const selectedActionOptions = useMemo(() => {
    if (!selectedFilters.actions) return [];
    const selectedValues = selectedFilters.actions.split(',').filter(Boolean);
    return actionOptions.filter((opt) => selectedValues.includes(opt.value));
  }, [actionOptions, selectedFilters.actions]);

  // NEW: Selected Completed By
  const selectedCompletedByOption = useMemo(
    () =>
      completedByOptions.find((opt) => opt.value === selectedFilters.completedBy) || null,
    [completedByOptions, selectedFilters.completedBy]
  );

  // NEW: Selected Role
  const selectedRoleOption = useMemo(
    () =>
      roleOptions.find((opt) => opt.value === selectedFilters.completedRole) || null,
    [roleOptions, selectedFilters.completedRole]
  );

  // Auto-select single building if there's only one
  useEffect(() => {
    if (
      buildingList.length === 1 &&
      !selectedFilters.building_name &&
      selectedFilters.property_id
    ) {
      onPropertySelectionChange({
        ...selectedFilters,
        building_name: buildingList[0],
      });
    }
  }, [buildingList, selectedFilters.property_id, selectedFilters.building_name, onPropertySelectionChange]);

  // Sync address & postal code when property is selected
  useEffect(() => {
    if (!currentProperty || !selectedFilters.property_id) return;

    const updated = {
      ...selectedFilters,
      address: currentProperty.address || selectedFilters.address || "",
      postal_code: currentProperty.postal_code || selectedFilters.postal_code || "",
    };

    if (
      updated.address !== selectedFilters.address ||
      updated.postal_code !== selectedFilters.postal_code
    ) {
      onPropertySelectionChange(updated);
    }
  }, [currentProperty, selectedFilters, onPropertySelectionChange]);

  const handlePropertyChange = useCallback(
    (selectedOption) => {
      const newId = selectedOption ? selectedOption.value : "";
      const prop = propertyList.find((p) => p._id === newId);

      onPropertySelectionChange({
        ...selectedFilters,
        property_id: newId,
        building_name: "",
        address: prop?.address || "",
        postal_code: prop?.postal_code || "",
      });
    },
    [propertyList, selectedFilters, onPropertySelectionChange]
  );

  const handleBuildingChange = useCallback(
    (selectedOption) => {
      onPropertySelectionChange({
        ...selectedFilters,
        building_name: selectedOption ? selectedOption.value : "",
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const handleActionsChange = useCallback(
    (selected) => {
      const values = selected ? selected.map((opt) => opt.value) : [];
      onPropertySelectionChange({
        ...selectedFilters,
        actions: values.join(','),
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  // NEW handlers
  const handleCompletedByChange = useCallback(
    (selected) => {
      onPropertySelectionChange({
        ...selectedFilters,
        completedBy: selected ? selected.value : '',
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const handleRoleChange = useCallback(
    (selected) => {
      onPropertySelectionChange({
        ...selectedFilters,
        completedRole: selected ? selected.value : '',
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const handleDateChange = useCallback(
    (key) => (e) => {
      onPropertySelectionChange({
        ...selectedFilters,
        [key]: e.target.value || "",
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const toggleFilters = () => setIsFiltersOpen((prev) => !prev);

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, { isDisabled }) => ({
      ...base,
      zIndex: 2,
      backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
      cursor: isDisabled ? "not-allowed" : "default",
    }),
  };

  const isGenerateDisabled = useMemo(() => {
    if (!selectedFilters.property_id) return true;
    if (buildingList.length > 0 && !selectedFilters.building_name) return true;
    return false;
  }, [selectedFilters.property_id, selectedFilters.building_name, buildingList.length]);

  return (
    <Form className="mb-4">
      {/* Main selection row */}
      <Row className="g-3 align-items-end">
        <Col md={4}>
          <FormGroup>
            <Label className="fw-semibold">Property</Label>
            <Select
              value={selectedPropertyOption}
              onChange={handlePropertyChange}
              options={propertyOptions}
              placeholder="Select Property"
              isClearable
              isDisabled={isLoading}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </FormGroup>
        </Col>

        {buildingList.length > 0 && (
          <Col md={4}>
            <FormGroup>
              <Label className="fw-semibold">Building Name</Label>
              <Select
                value={selectedBuildingOption}
                onChange={handleBuildingChange}
                options={buildingOptions}
                placeholder="Select Building"
                isClearable={buildingList.length > 1}
                isDisabled={buildingList.length === 1 || isLoading}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>
        )}

        <Col md={buildingList.length > 0 ? 4 : 8}>
          <div
            className="d-flex align-items-center gap-2 flex-wrap"
            style={{
              justifyContent: 'flex-end',
              height: '100%',
            }}
          >
            <div className="d-flex gap-2">
              <Button
                color="primary"
                className="flex-fill"
                onClick={onAddClick}
                disabled={isGenerateDisabled || isLoading}
                style={{ minWidth: '140px' }}
              >
                <FeatherIcon icon="plus-circle" size={18} className="me-2" />
                Generate Log
              </Button>

              <Button
                color="success"
                className="flex-fill"
                onClick={onExportExcel}
                disabled={isGenerateDisabled || isLoading || !selectedFilters.property_id}
                style={{ minWidth: '140px' }}
              >
                <FeatherIcon icon="download" size={18} className="me-2" />
                Export Excel
              </Button>
            </div>
            <Button
              color="primary"
              outline
              onClick={toggleFilters}
              disabled={isLoading}
              style={{ minWidth: '140px' }}
            >
              <FeatherIcon icon="filter" size={18} className="me-2" />
              More Filters
            </Button>
          </div>
        </Col>
      </Row>

      <Collapse isOpen={isFiltersOpen}>
        <Row className="g-3 mt-3">
          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">From Date</Label>
              <Input
                type="date"
                value={selectedFilters.startDate || ""}
                onChange={handleDateChange("startDate")}
                disabled={isLoading}
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">To Date</Label>
              <Input
                type="date"
                value={selectedFilters.endDate || ""}
                onChange={handleDateChange("endDate")}
                disabled={isLoading}
                min={selectedFilters.startDate || undefined}
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">Completed By</Label>
              <Select
                value={selectedCompletedByOption}
                onChange={handleCompletedByChange}
                options={completedByOptions}
                placeholder="Any user"
                isClearable
                isDisabled={isLoading || completedByOptions.length === 0}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">Role</Label>
              <Select
                value={selectedRoleOption}
                onChange={handleRoleChange}
                options={roleOptions}
                placeholder="Any role"
                isClearable
                isDisabled={isLoading}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label className="fw-semibold">Actions Performed</Label>
              <Select
                isMulti
                value={selectedActionOptions}
                onChange={handleActionsChange}
                options={actionOptions}
                placeholder="Filter by actions..."
                isClearable
                isDisabled={isLoading || actionOptions.length === 0}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
              />
            </FormGroup>
          </Col>
        </Row>
      </Collapse>
    </Form>
  );
};

export { PropertySelector };